import React, { useEffect, useRef, useState } from 'react';
import QRCode from 'qrcode';
import './QRCodeGenerator.css';

interface QRCodeGeneratorProps {
  value: string;
  size?: number;
  className?: string;
  responsive?: boolean; // 新增响应式选项
}

const QRCodeGenerator: React.FC<QRCodeGeneratorProps> = ({ 
  value, 
  size = 200, 
  className = '',
  responsive = false
}) => {
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const containerRef = useRef<HTMLDivElement>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [actualSize, setActualSize] = useState(size);

  // 响应式尺寸计算
  useEffect(() => {
    if (!responsive || !containerRef.current) {
      setActualSize(size);
      return;
    }

    const updateSize = () => {
      if (containerRef.current) {
        const containerWidth = containerRef.current.offsetWidth;
        const containerHeight = containerRef.current.offsetHeight;
        const minSize = Math.min(containerWidth, containerHeight) - 16; // 减去padding
        setActualSize(Math.max(100, Math.min(minSize, 400))); // 限制在100-400px之间
      }
    };

    // 初始化尺寸
    updateSize();

    // 监听窗口大小变化
    const resizeObserver = new ResizeObserver(updateSize);
    resizeObserver.observe(containerRef.current);

    return () => {
      resizeObserver.disconnect();
    };
  }, [responsive, size]);

  useEffect(() => {
    const generateQRCode = async () => {
      if (!canvasRef.current) return;

      setIsLoading(true);
      setError(null);

      try {
        await QRCode.toCanvas(canvasRef.current, value, {
          width: actualSize,
          margin: 1,
          color: {
            dark: '#2c3e50',  // 深色部分
            light: '#ffffff'  // 背景色
          },
          errorCorrectionLevel: 'M'
        });
        setIsLoading(false);
      } catch (err) {
        console.error('Failed to generate QR code:', err);
        setError('二维码生成失败');
        setIsLoading(false);
      }
    };

    generateQRCode();
  }, [value, actualSize]);

  return (
    <div 
      ref={containerRef}
      className={`qr-code-generator ${responsive ? 'qr-responsive' : ''} ${className}`}
    >
      {isLoading && (
        <div className="qr-loading-placeholder" style={{ 
          width: responsive ? '100%' : actualSize, 
          height: responsive ? '100%' : actualSize,
          aspectRatio: responsive ? '1' : 'auto'
        }}>
          <div className="qr-loading-spinner"></div>
        </div>
      )}
      {error && (
        <div className="qr-error-placeholder" style={{ 
          width: responsive ? '100%' : actualSize, 
          height: responsive ? '100%' : actualSize,
          aspectRatio: responsive ? '1' : 'auto'
        }}>
          <span>⚠️</span>
          <p>{error}</p>
        </div>
      )}
      <canvas
        ref={canvasRef}
        className={`qr-canvas ${responsive ? 'qr-canvas-responsive' : ''}`}
        style={{
          display: isLoading || error ? 'none' : 'block',
          borderRadius: '8px',
          width: responsive ? '100%' : 'auto',
          height: responsive ? '100%' : 'auto',
          maxWidth: responsive ? '100%' : 'none',
          maxHeight: responsive ? '100%' : 'none'
        }}
      />
    </div>
  );
};

export default QRCodeGenerator;
